import { ReactNode, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import { ChevronDown, ChevronUp } from "lucide-react";

export interface MobileCardField {
  /** Label for the field */
  label: string;
  /** Value to display */
  value: ReactNode;
  /** Whether this is the primary field (shown larger) */
  primary?: boolean;
}

export interface MobileCardProps {
  /** Primary title (displayed prominently) */
  title: ReactNode;
  /** Optional subtitle */
  subtitle?: ReactNode;
  /** Fields to display in the card body */
  fields?: MobileCardField[];
  /** Expandable detail content */
  expandedContent?: ReactNode;
  /** Action buttons (positioned at bottom for thumb access) */
  actions?: ReactNode;
  /** Optional status chip or indicator */
  status?: ReactNode;
  /** Click handler for the entire card */
  onClick?: () => void;
  /** Whether the card is selected/active */
  selected?: boolean;
}

/**
 * MobileCard component for responsive table mobile view.
 * Designed for thumb-friendly interaction with:
 * - Primary info at top
 * - Expandable details in middle
 * - Action buttons at bottom (thumb zone)
 *
 * @example
 * <MobileCard
 *   title={contact.name}
 *   subtitle={contact.email}
 *   fields={[
 *     { label: 'Suburb', value: contact.suburb },
 *     { label: 'Frequency', value: contact.frequency },
 *   ]}
 *   expandedContent={<ContactDetails contact={contact} />}
 *   actions={
 *     <>
 *       <Button>Edit</Button>
 *       <Button color="error">Delete</Button>
 *     </>
 *   }
 *   status={<Chip label="Active" color="success" size="small" />}
 * />
 */
export function MobileCard({
  title,
  subtitle,
  fields,
  expandedContent,
  actions,
  status,
  onClick,
  selected,
}: MobileCardProps) {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        borderColor: selected ? "primary.main" : "divider",
        borderWidth: selected ? 2 : 1,
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&:hover": onClick
          ? {
              borderColor: "primary.light",
              boxShadow: 1,
            }
          : undefined,
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Header: Title + Status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
          {status && <Box sx={{ ml: 1, flexShrink: 0 }}>{status}</Box>}
        </Box>

        {/* Fields */}
        {fields && fields.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {fields.map((field, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {field.label}
                </Typography>
                <Typography
                  component="div"
                  variant="body2"
                  fontWeight={field.primary ? 600 : 400}
                  sx={{ textAlign: "right" }}
                >
                  {field.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Expandable content toggle */}
        {expandedContent && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={handleExpandClick}
                aria-expanded={expanded}
                aria-label={expanded ? "Show less" : "Show more"}
                sx={{ color: "text.secondary" }}
              >
                {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </IconButton>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ pt: 1 }}>{expandedContent}</Box>
            </Collapse>
          </>
        )}

        {/* Actions - positioned at bottom for thumb zone */}
        {actions && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {actions}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
